import api from '../api';
import RuntimeStatus from '../components/RuntimeStatus';
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Grid,
  CircularProgress,
  IconButton,
  Tooltip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ToggleButton,
  ToggleButtonGroup,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  RestartAlt as RestartIcon,
  Schedule as ScheduleIcon,
  Memory as WorkerIcon,
  Storage as RedisIcon,
  Code as CodeIcon,
  Article as LogsIcon,
  OpenInNew as OpenInNewIcon,
  Dashboard as DashboardIcon,
  RocketLaunch as DeployIcon,
  PlayArrow as PlayIcon,
  WhatsApp as WhatsAppIcon,
} from '@mui/icons-material';
import { infraAPI, instanciasAPI } from '../api';
import toast from 'react-hot-toast';

interface DeploymentInfo {
  status: string;
  image: string;
  imageId: string;
  replicas: number;
  readyReplicas: number;
  restarts: number;
  podCreated: string | null;
  lastRestart: string | null;
}

interface ImageInfo {
  image: string;
  imageId: string;
}

type LogLevelFilter = 'all' | 'error' | 'warn' | 'info' | 'http';
interface LogEvent {
  lines: string[];
  text: string;
  level: Exclude<LogLevelFilter, 'all'> | 'default';
}

export default function Infraestrutura() {
  const [loading, setLoading] = useState(true);
  const [restarting, setRestarting] = useState<string | null>(null);
  const [deploying, setDeploying] = useState<string | null>(null);
  const [logsOpen, setLogsOpen] = useState(false);
  const [logsDeployment, setLogsDeployment] = useState('');
  const [logsLabel, setLogsLabel] = useState('');
  const [logsContent, setLogsContent] = useState('');
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsPeriod, setLogsPeriod] = useState('tail');
  const [logsSearch, setLogsSearch] = useState('');
  const [logsLevel, setLogsLevel] = useState<LogLevelFilter>('all');
  const logsBoxRef = useRef<HTMLDivElement>(null);
  const logsEndRef = useRef<HTMLDivElement>(null);
  const shouldStickToBottomRef = useRef(true);

  // Trigger job state
  const [triggerJobName, setTriggerJobName] = useState('');
  const [triggerAlias, setTriggerAlias] = useState('');
  const [reconsolidationDays, setReconsolidationDays] = useState<number>(3);
  const [triggering, setTriggering] = useState(false);
  const [instancias, setInstancias] = useState<{ _id: string; nome: string; namespace_k8s?: string }[]>([]);

  const AVAILABLE_JOBS = [
    { value: 'StatusSyncWorker', label: 'Sync Status Usinas (*/15 min)' },
    { value: 'DailyPerformanceWorker', label: 'Performance Diária (00:10)' },
    { value: 'DailyMaintenanceJob', label: 'Manutenção Planos (01:00)' },
    { value: 'WebhookVerificationJob', label: 'Verificação Webhook Inter (03:00)' },
    { value: 'BoletoReconciliation', label: 'Reconciliação Boletos (03:30)' },
    { value: 'OverdueServiceOrders', label: 'OSs Atrasadas (02:00)' },
    { value: 'OverdueInvoicesStatus', label: 'Faturas Atrasadas (00:30)' },
    { value: 'MongoBackup', label: 'Backup MongoDB (Sex 02:30)' },
    { value: 'NoInternetFollowUp', label: 'Follow-up Sem Internet (Seg 08:00)' },
    { value: 'OfflineProlongedAlert', label: 'Alerta Offline +3 dias (10:00)' },
    { value: 'InvoiceNotifications', label: 'Notificações Faturas (09:30 úteis)' },
    { value: 'CertExpiryCheck', label: 'Verificação Certificado Inter (08:00)' },
    { value: 'ReconsolidatePerformance', label: 'Re-consolidar Performance' },
  ];

  const [infra, setInfra] = useState<{
    scheduler: DeploymentInfo;
    worker: DeploymentInfo;
    redis: DeploymentInfo;
    evolution: DeploymentInfo;
    images: {
      backend: ImageInfo;
      frontend: ImageInfo;
    };
  } | null>(null);

  const carregarInfo = useCallback(async () => {
    setLoading(true);
    try {
      const res = await infraAPI.obterInfo();
      setInfra(res.data);
    } catch (error) {
      toast.error('Erro ao carregar info da infraestrutura');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    carregarInfo();
  }, [carregarInfo]);

  useEffect(() => {
    instanciasAPI.listar().then(res => setInstancias(res.data.data)).catch(() => {});
  }, []);

  const handleTriggerJob = async () => {
    if (!triggerJobName) return;
    setTriggering(true);
    try {
      const alias = triggerAlias || undefined;
      const options = triggerJobName === 'ReconsolidatePerformance'
        ? { days: reconsolidationDays }
        : undefined;
      const res = await infraAPI.triggerJob(triggerJobName, alias, options);
      toast.success(res.data.message || 'Job executado com sucesso!');
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Erro ao executar job');
    } finally {
      setTriggering(false);
    }
  };

  const handleRestart = async (deployment: string, label: string) => {
    if (!window.confirm(`Reiniciar ${label}?`)) return;

    setRestarting(deployment);
    try {
      await infraAPI.restart(deployment);
      toast.success(`${label} reiniciado com sucesso!`);
      setTimeout(carregarInfo, 3000);
    } catch (error) {
      toast.error(`Erro ao reiniciar ${label}`);
    } finally {
      setRestarting(null);
    }
  };

  const handleDeploy = async (deployment: string, label: string) => {
    if (!window.confirm(`Criar deployment ${label} no cluster?`)) return;

    setDeploying(deployment);
    try {
      await infraAPI.deploy(deployment);
      toast.success(`${label} criado com sucesso!`);
      setTimeout(carregarInfo, 5000);
    } catch (error: any) {
      toast.error(error.response?.data?.error || `Erro ao criar ${label}`);
    } finally {
      setDeploying(null);
    }
  };

  const fetchLogs = async (deployment: string, period: string) => {
    setLogsLoading(true);
    try {
      const wasAtBottom = shouldStickToBottomRef.current;
      const sinceSeconds = period !== 'tail' ? Number(period) : undefined;
      const res = await infraAPI.logs(deployment, 300, sinceSeconds);
      setLogsContent(res.data.logs);
      if (wasAtBottom) {
        setTimeout(() => logsEndRef.current?.scrollIntoView({ block: 'end' }), 40);
      }
    } catch {
      setLogsContent('[Erro ao buscar logs]');
    } finally {
      setLogsLoading(false);
    }
  };

  const handleOpenLogs = async (deployment: string, label: string) => {
    setLogsDeployment(deployment);
    setLogsLabel(label);
    setLogsOpen(true);
    setLogsPeriod('tail');
    setLogsSearch('');
    setLogsLevel('all');
    shouldStickToBottomRef.current = true;
    await fetchLogs(deployment, 'tail');
  };

  const handleRefreshLogs = () => fetchLogs(logsDeployment, logsPeriod);

  // Auto-refresh a cada 5s enquanto dialog está aberto
  useEffect(() => {
    if (!logsOpen || !logsDeployment) return;
    const interval = setInterval(() => {
      fetchLogs(logsDeployment, logsPeriod);
    }, 5000);
    return () => clearInterval(interval);
  }, [logsOpen, logsDeployment, logsPeriod]);

  const handlePeriodChange = (_: any, newPeriod: string | null) => {
    if (!newPeriod) return;
    setLogsPeriod(newPeriod);
    fetchLogs(logsDeployment, newPeriod);
  };

  const classifyLogLine = useCallback((line: string): Exclude<LogLevelFilter, 'all'> | 'default' => {
    if (/\b(error|err|fatal|critical|exception)\b/i.test(line)) return 'error';
    if (/\b(warn|warning)\b/i.test(line)) return 'warn';
    if (/\b(get|post|put|patch|delete)\b\s+\//i.test(line)) return 'http';
    if (/\b(info|debug|trace)\b/i.test(line)) return 'info';
    return 'default';
  }, []);

  const isTimestampLine = useCallback((line: string) => {
    return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(line);
  }, []);

  const processedLogs = useMemo<LogEvent[]>(() => {
    const lines = logsContent ? logsContent.split('\n') : [];
    const query = logsSearch.trim().toLowerCase();
    const events: LogEvent[] = [];
    for (const line of lines) {
      if (!line.trim()) continue;
      if (events.length === 0 || isTimestampLine(line)) {
        events.push({ lines: [line], text: line, level: classifyLogLine(line) });
        continue;
      }
      const cur = events[events.length - 1];
      cur.lines.push(line);
      cur.text = `${cur.text}\n${line}`;
      if (cur.level === 'default') cur.level = classifyLogLine(line);
    }
    return events.filter((e) => {
      if (logsLevel !== 'all' && e.level !== logsLevel) return false;
      if (!query) return true;
      return e.text.toLowerCase().includes(query);
    });
  }, [logsContent, logsSearch, logsLevel, classifyLogLine, isTimestampLine]);

  const getLogLineColor = useCallback((level: Exclude<LogLevelFilter, 'all'> | 'default') => {
    if (level === 'error') return '#ff8a80';
    if (level === 'warn') return '#ffd180';
    if (level === 'info') return '#b39ddb';
    if (level === 'http') return '#80cbc4';
    return '#d4d4d4';
  }, []);

  // Bull Board agora está no management API (não mais no backend das instâncias)
  const bullBoardUrl = '/api/admin/queues';

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleString('pt-BR');
  };

  const getStatusColor = (status: string): 'success' | 'error' | 'warning' | 'info' => {
    if (status === 'Running') return 'success';
    if (status === 'Pending') return 'warning';
    if (status === 'Not Found' || status === 'Failed') return 'error';
    return 'info';
  };

  const extractDigest = (imageId: string): string => {
    if (!imageId) return 'N/A';
    const parts = imageId.split('@sha256:');
    return parts.length > 1 ? parts[1].substring(0, 12) : imageId.substring(0, 20);
  };

  if (loading && !infra) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  const deployments = [
    {
      key: 'solar-scheduler',
      label: 'Scheduler (Cron Jobs)',
      icon: <ScheduleIcon sx={{ fontSize: 40, color: '#1976d2' }} />,
      data: infra?.scheduler,
      description: 'Processo isolado que executa todos os cron jobs (sync, backup, lembretes, etc). Sempre replicas: 1.',
    },
    {
      key: 'solar-worker',
      label: 'Heavy Worker (BullMQ + PDFs)',
      icon: <WorkerIcon sx={{ fontSize: 40, color: '#ff9800' }} />,
      data: infra?.worker,
      description: 'Processa filas BullMQ (faturas, notificações, relatórios) e gera PDFs via Puppeteer.',
    },
    {
      key: 'redis',
      label: 'Redis',
      icon: <RedisIcon sx={{ fontSize: 40, color: '#d32f2f' }} />,
      data: infra?.redis,
      description: 'Broker de filas BullMQ compartilhado entre todos os tenants.',
    },
    {
      key: 'evolution-api',
      label: 'Evolution API (WhatsApp)',
      icon: <WhatsAppIcon sx={{ fontSize: 40, color: '#25d366' }} />,
      data: infra?.evolution,
      description: 'Gateway WhatsApp compartilhado. Cada instância solar tem sua própria instância Baileys dentro deste pod.',
    },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" fontWeight="bold">
          Infraestrutura
        </Typography>
        <Tooltip title="Atualizar">
          <IconButton onClick={carregarInfo} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Imagens em uso */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <CodeIcon color="primary" />
            <Typography variant="h6">Imagens Docker em Uso</Typography>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant="subtitle2" color="textSecondary">Backend</Typography>
                <Typography variant="body2" fontFamily="monospace" sx={{ mt: 0.5 }}>
                  {infra?.images?.backend?.image || 'N/A'}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Digest: {extractDigest(infra?.images?.backend?.imageId || '')}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant="subtitle2" color="textSecondary">Frontend</Typography>
                <Typography variant="body2" fontFamily="monospace" sx={{ mt: 0.5 }}>
                  {infra?.images?.frontend?.image || 'N/A'}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Digest: {extractDigest(infra?.images?.frontend?.imageId || '')}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <RuntimeStatus />
      {/* Deployments */}
      <Grid container spacing={3}>
        {deployments.map((dep) => (
          <Grid item xs={12} md={4} key={dep.key}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  {dep.icon}
                  <Chip
                    label={dep.data?.status || 'Unknown'}
                    color={getStatusColor(dep.data?.status || 'Unknown')}
                    size="small"
                  />
                </Box>

                <Typography variant="h6" gutterBottom>
                  {dep.label}
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2, minHeight: 40 }}>
                  {dep.description}
                </Typography>

                <Divider sx={{ my: 1.5 }} />

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <Typography variant="body2">
                    <strong>Imagem:</strong>{' '}
                    <Typography component="span" variant="body2" fontFamily="monospace" fontSize={11}>
                      {dep.data?.image || 'N/A'}
                    </Typography>
                  </Typography>
                  <Typography variant="body2">
                    <strong>Digest:</strong>{' '}
                    <Typography component="span" variant="body2" fontFamily="monospace" fontSize={11}>
                      {extractDigest(dep.data?.imageId || '')}
                    </Typography>
                  </Typography>
                  <Typography variant="body2">
                    <strong>Replicas:</strong> {dep.data?.readyReplicas || 0}/{dep.data?.replicas || 0}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Restarts:</strong> {dep.data?.restarts || 0}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Pod criado:</strong> {formatDate(dep.data?.podCreated || null)}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Último restart:</strong> {formatDate(dep.data?.lastRestart || null)}
                  </Typography>
                </Box>

                <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {dep.data?.status === 'Not Found' && dep.key !== 'redis' ? (
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      startIcon={<DeployIcon />}
                      onClick={() => handleDeploy(dep.key, dep.label)}
                      disabled={deploying === dep.key}
                      fullWidth
                    >
                      {deploying === dep.key ? 'Criando...' : 'Deploy'}
                    </Button>
                  ) : (
                    <>
                      <Button
                        variant="outlined"
                        color="warning"
                        size="small"
                        startIcon={<RestartIcon />}
                        onClick={() => handleRestart(dep.key, dep.label)}
                        disabled={restarting === dep.key}
                        sx={{ flex: 1 }}
                      >
                        {restarting === dep.key ? 'Reiniciando...' : 'Reiniciar'}
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<LogsIcon />}
                        onClick={() => handleOpenLogs(dep.key, dep.label)}
                        sx={{ flex: 1 }}
                      >
                        Logs
                      </Button>
                    </>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Bull Board */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', flexDirection: {xs:'column',sm:'row'}, gap: 2, alignItems: {xs:'stretch',sm:'center'}, justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <DashboardIcon color="primary" />
              <Box>
                <Typography variant="h6">Bull Board — Filas BullMQ</Typography>
                <Typography variant="body2" color="textSecondary">
                  Visualize e gerencie as filas de processamento (faturas, cancelamentos, notificações). 
                  Todas as instâncias compartilham o mesmo Redis, então qualquer Bull Board mostra todas as filas.
                </Typography>
              </Box>
            </Box>
            {bullBoardUrl ? (
              <Button
                variant="contained"
                startIcon={<OpenInNewIcon />}
                onClick={async()=>{const tab=window.open("about:blank","_blank");try{await api.post("/auth/queue-access");if(tab){tab.opener=null;tab.location.href=bullBoardUrl;}}catch{tab?.close();}}}
              >
                Abrir Bull Board
              </Button>
            ) : (
              <Typography variant="body2" color="textSecondary">Nenhuma instância ativa</Typography>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Trigger Manual de Jobs */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <PlayIcon color="primary" />
            <Typography variant="h6">Trigger Manual de Job</Typography>
          </Box>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Execute um job do scheduler sob demanda. Selecione a instância para rodar em um tenant específico, ou deixe em branco para rodar em todos.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: {xs:0,sm:300}, width: {xs:'100%',sm:'auto'} }}>
              <InputLabel>Job</InputLabel>
              <Select value={triggerJobName} onChange={(e) => setTriggerJobName(e.target.value)} label="Job">
                {AVAILABLE_JOBS.map((j) => (
                  <MenuItem key={j.value} value={j.value}>{j.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            {triggerJobName === 'ReconsolidatePerformance' && (
              <FormControl size="small" sx={{ minWidth: {xs:0,sm:220}, width: {xs:'100%',sm:'auto'} }}>
                <InputLabel>Período</InputLabel>
                <Select
                  value={String(reconsolidationDays)}
                  onChange={(e) => setReconsolidationDays(Number(e.target.value))}
                  label="Período"
                >
                  <MenuItem value="3">Últimos 3 dias</MenuItem>
                  <MenuItem value="7">Últimos 7 dias</MenuItem>
                  <MenuItem value="30">Últimos 30 dias</MenuItem>
                </Select>
              </FormControl>
            )}
            <FormControl size="small" sx={{ minWidth: {xs:0,sm:200}, width: {xs:'100%',sm:'auto'} }}>
              <InputLabel>Instância (opcional)</InputLabel>
              <Select value={triggerAlias} onChange={(e) => setTriggerAlias(e.target.value)} label="Instância (opcional)">
                <MenuItem value="">Todos os tenants</MenuItem>
                {instancias.map((inst) => (
                  <MenuItem key={inst._id} value={inst.namespace_k8s?.replace(/^solar-/, '') || inst.nome}>
                    {inst.nome}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="contained"
              startIcon={triggering ? <CircularProgress size={16} color="inherit" /> : <PlayIcon />}
              onClick={handleTriggerJob}
              disabled={!triggerJobName || triggering}
            >
              {triggering ? 'Executando...' : 'Executar'}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Dialog de Logs */}
      <Dialog open={logsOpen} onClose={() => setLogsOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
            <LogsIcon fontSize="small" />
            <Typography variant="h6">Logs — {logsLabel}</Typography>
            {logsLoading && <CircularProgress size={14} sx={{ ml: 1 }} />}
          </Box>
          <IconButton onClick={handleRefreshLogs} disabled={logsLoading} size="small">
            <RefreshIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <ToggleButtonGroup value={logsPeriod} exclusive onChange={handlePeriodChange} size="small">
              <ToggleButton value="tail">Últimas 300</ToggleButton>
              <ToggleButton value="900">15 min</ToggleButton>
              <ToggleButton value="3600">1 h</ToggleButton>
              <ToggleButton value="21600">6 h</ToggleButton>
              <ToggleButton value="86400">24 h</ToggleButton>
            </ToggleButtonGroup>
            <ToggleButtonGroup value={logsLevel} exclusive onChange={(_, v) => v && setLogsLevel(v)} size="small">
              <ToggleButton value="all">Todos</ToggleButton>
              <ToggleButton value="http">HTTP</ToggleButton>
              <ToggleButton value="error">Erro</ToggleButton>
              <ToggleButton value="warn">Warn</ToggleButton>
              <ToggleButton value="info">Info</ToggleButton>
            </ToggleButtonGroup>
            <TextField
              size="small"
              label="Filtrar logs"
              value={logsSearch}
              onChange={(e) => setLogsSearch(e.target.value)}
              sx={{ minWidth: {xs:0,sm:220}, width: {xs:'100%',sm:'auto'} }}
            />
          </Box>
          {logsLoading && !logsContent ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
          ) : (
            <Box
              ref={logsBoxRef}
              onScroll={() => {
                const el = logsBoxRef.current;
                if (!el) return;
                shouldStickToBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 24;
              }}
              sx={{
                bgcolor: '#1e1e1e',
                color: '#d4d4d4',
                fontFamily: 'monospace',
                fontSize: 12,
                p: 2,
                borderRadius: 1,
                maxHeight: '60vh',
                overflow: 'auto',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-all',
              }}
            >
              {processedLogs.length === 0 ? (
                '[Sem logs para os filtros selecionados]'
              ) : (
                processedLogs.map((event, index) => (
                  <Box key={`${index}-${event.lines[0]?.slice(0, 24) || 'ev'}`} sx={{ mb: 1.5 }}>
                    {event.lines.map((line, li) => (
                      <Box key={`${index}-${li}`} sx={{ color: getLogLineColor(event.level) }}>
                        {line}
                      </Box>
                    ))}
                  </Box>
                ))
              )}
              <div ref={logsEndRef} />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLogsOpen(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
